/* eslint-disable @typescript-eslint/no-unused-vars */
import classNames from "classnames";
import { usePoisBountyContext } from "components/PoisBountyWidgetV3/context";
import { toTimeMs } from "components/PoisBountyWidgetV3/hooks/helper";
import { Indicator } from "components/PoisBountyWidgetV3/lib/common";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { useAppDispatch, useAppSelector, usePopupsState } from "hooks";
import { useEffect, useMemo, useState } from "react";
import {
	useCreateCycleTrackerMutation,
	usePostPbMetadataMutation
} from "redux/apis";
import {
	onOpenPopUp,
	onPBV3change,
	selectPoisBountyV3State
} from "redux/slices";
import { nFormatter } from "utils/number-formatter";
import { getTimeLeft } from "utils/time";
import { Marker, StatusIcon, Target } from "..";
import { StepContext } from "./context";
import {
	DEFAULT_CONFIG,
	getPrizeClassNames,
	getProgressBarWidth,
	getProgressClassNames,
	getRewardItemClassNames,
	getStepClassNames,
	getTextClassNames
} from "./helper";
import {
	Bar,
	Percentage,
	ProgressRewardStep,
	Reward,
	RewardImage,
	RewardItem,
	RewardPrizeImage,
	RewardText,
	StepStyled
} from "./styles";
import { StepProps } from "./types";

export const Step = ({
	reward,
	stepIndex,
	config = DEFAULT_CONFIG
}: StepProps): JSX.Element => {
	const dispatch = useAppDispatch();
	const { hideBar, first, last } = config;
	const { nowTime } = useWebStoreContext();
	const [postPbMetadata] = usePostPbMetadataMutation();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const [createCycleTracker] = useCreateCycleTrackerMutation();
	const { isPortrait, isPageVariant } = usePoisBountyContext();
	const {
		isAnyPopupOpen,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	} = usePopupsState();
	const { lp, rewards, tracker, trackers } = useAppSelector(
		selectPoisBountyV3State
	);
	const { target, status, rewardAmount, imageUrl, claimedDate } = reward;

	const sawUncollectedRewardsPopUp = useMemo(() => {
		return trackers?.userData?.cycleData?.[tracker?.cycleIndex ?? 0]?.metadata
			?.sawUncollectedRewardsPopUp;
	}, [
		trackers?.userData?.cycleData?.[tracker?.cycleIndex ?? 0]?.metadata
			?.sawUncollectedRewardsPopUp
	]);

	const isLocked = useMemo(() => {
		return status === "locked";
	}, [status]);

	const hasSeenClaimedPopup = useMemo(() => {
		return (
			Number(
				trackers?.userData?.cycleData?.[tracker?.cycleIndex || 0]?.metadata
					?.lastRewardClaimed || -1
			) >= stepIndex
		);
	}, [
		tracker?.cycleIndex,
		trackers?.userData?.cycleData?.[tracker?.cycleIndex || 0]?.metadata
			?.lastRewardClaimed
	]);

	const onClaim = async () => {
		const isLastClaimedReward =
			stepIndex === rewards?.filter((r) => r.claimed).length - 1;

		if (hasSeenClaimedPopup || !tracker || !isLastClaimedReward) {
			return;
		}
		await postPbMetadata({
			eventId: tracker.eventId,
			key: "lastRewardClaimed",
			value: stepIndex,
			cycleIndex: tracker.cycleIndex
		})
			.unwrap()
			.then(async () => {
				const rewardsToClaim = rewards?.filter(
					(r) => r?.claimed === claimedDate
				);
				const hasMoreThanOneRewardToClaim = rewardsToClaim?.length > 1;
				if (hasMoreThanOneRewardToClaim) {
					const userQualifiesForAll = rewardsToClaim.every((_, idx) => {
						const rewardIndex = rewards.indexOf(rewardsToClaim[idx]);
						return tracker?.rewardClaimStatuses?.[
							rewardIndex
						]?.rewardedUserIds?.find(
							(uid) => uid === trackers?.userData?.userId
						);
					});
					if (userQualifiesForAll) {
						dispatch(onOpenPopUp("pb-v3-rewards-uncollected"));
					}
				} else {
					const userHasReward = tracker?.rewardClaimStatuses?.[
						stepIndex
					]?.rewardedUserIds?.find((uid) => uid === trackers?.userData?.userId);
					if (userHasReward) {
						dispatch(onPBV3change({ rewardCollected: reward }));
						dispatch(onOpenPopUp("pb-reward-collected-v3"));
					}
				}
				const timeLeft = getTimeLeft(nowTime, tracker?.endTime, "seconds");
				const timeTotal =
					getTimeLeft(tracker?.startTime, tracker?.endTime, "seconds") ||
					undefined;
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "claim_reward",
					event_type: "pois_bounty_v3",
					action: "claim_reward",
					source: "pois_bounty_v3",
					type: trackers?.eventType === "user" ? "personal" : "club",
					time_left: timeLeft,
					time_total: timeTotal,
					event_id: tracker?.eventId,
					reward_id: rewards?.[stepIndex]?.reward?.rewardId,
					node_type: (stepIndex + 1)?.toString(),
					node_type2: tracker?.cycleIndex?.toString(),
					value_int: rewardAmount,
					confignodecount: stepIndex + 1,
					loopcount: tracker?.cycleIndex
				});
				await postPbMetadata({
					eventId: trackers?.eventId ?? "",
					key: "sawUncollectedRewardsPopUp",
					value: nowTime?.toISOString() || new Date().toISOString(),
					cycleIndex: tracker?.cycleIndex || 0
				}).unwrap();
			})
			.finally(() => {
				dispatch(onPBV3change({ rewardToClaim: undefined }));
			});
	};

	const resetCycle = async () => {
		if (tracker) {
			await postPbMetadata({
				eventId: tracker.eventId,
				key: "popupDisplayed",
				value: true,
				cycleIndex: tracker.cycleIndex
			}).unwrap();
			const maxCycles = tracker.maxCycles;
			const hasMoreCycles =
				maxCycles === undefined ||
				maxCycles === null ||
				Number(maxCycles) > Number(tracker.cycleIndex) + 1;

			// if last cycle is completed due to hitting just enough LP, create new cycle
			if (tracker.completed && hasMoreCycles) {
				await createCycleTracker({ eventId: tracker.eventId })
					.unwrap()
					.then(() => {
						dispatch(onOpenPopUp("pb-v3-cycle-complete"));
					});
			} else {
				// verify it's still not last cycle
				if (hasMoreCycles) {
					dispatch(onOpenPopUp("pb-v3-cycle-complete"));
				}
			}
		}
	};

	useEffect(() => {
		if (isPageVariant) {
			return;
		}
		dispatch(onPBV3change({ hasSeenClaimedPopup }));
	}, [hasSeenClaimedPopup]);

	useEffect(() => {
		if (
			!isPageVariant &&
			status === "unlocked" &&
			rewards.length > 0 &&
			lp > 0 &&
			!hasSeenClaimedPopup &&
			!isAnyPopupOpen &&
			!IsInAppToWebDipChecking &&
			!IsFriendReferralAcceptInviteChecking
		) {
			dispatch(onPBV3change({ rewardToClaim: reward }));
			onClaim();
		}
	}, [
		status,
		rewards,
		lp,
		hasSeenClaimedPopup,
		tracker,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	]);

	useEffect(() => {
		if (
			isPageVariant ||
			!last ||
			isAnyPopupOpen ||
			IsInAppToWebDipChecking ||
			IsFriendReferralAcceptInviteChecking
		) {
			return;
		}
		const nowTimeMs = toTimeMs(nowTime || new Date());
		const lastSawTimeMs = toTimeMs(sawUncollectedRewardsPopUp);
		const hasPassedOneHourSinceLastPopup =
			!Number.isNaN(nowTimeMs) &&
			!Number.isNaN(lastSawTimeMs) &&
			nowTimeMs - lastSawTimeMs > 60 * 60 * 1000;

		if (
			hasSeenClaimedPopup &&
			tracker?.completed &&
			sawUncollectedRewardsPopUp &&
			!hasPassedOneHourSinceLastPopup
		) {
			resetCycle();
		}
	}, [
		hasSeenClaimedPopup,
		tracker?.completed,
		trackers?.cycleTrackers,
		sawUncollectedRewardsPopUp,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	]);

	const values = useMemo(() => ({ target, last, status }), [status, trackers]);

	return (
		<StepContext.Provider value={values}>
			<StepStyled
				className={classNames(
					getStepClassNames(isPortrait, isPageVariant, first)
				)}
				steps={tracker?.rewardClaimStatuses.length || 7}
			>
				{/* 🎁 Reward:  item, marker, target and claim button */}
				<Reward className="reward">
					<RewardItem
						className={classNames(
							getRewardItemClassNames(status, isPageVariant)
						)}
					>
						<StatusIcon status={status} />
						<RewardImage
							background={"/spec_pois_bounty/item-glow.png"}
							color={tracker?.rewardSlotColorHex}
						>
							<RewardPrizeImage
								src={imageUrl}
								status={status}
								className={getPrizeClassNames(status)}
							/>
							{rewardAmount && (
								<RewardText
									style={{ paintOrder: "stroke fill" }}
									className={classNames(
										getTextClassNames(status, isPageVariant)
									)}
								>
									{!rewards?.[stepIndex]?.reward?.rewardId.includes("coin") &&
										"x"}
									{nFormatter(rewardAmount)}
								</RewardText>
							)}
						</RewardImage>
					</RewardItem>
					<Marker />
					<Target />
				</Reward>

				{/* 📊 Reward Progress Bar */}
				<ProgressRewardStep
					className={classNames(
						getProgressClassNames(first, hideBar, isPageVariant)
					)}
				>
					<Bar className="reward-bar">
						{!hideBar && (
							<Percentage
								className={classNames(first && "first")}
								style={{
									width: getProgressBarWidth(status, lp, target, isPageVariant)
								}}
							>
								{isLocked && (
									<Indicator lastStep={last} userLoyaltyPoints={lp} />
								)}
							</Percentage>
						)}
					</Bar>
				</ProgressRewardStep>
			</StepStyled>
		</StepContext.Provider>
	);
};
